import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseMutationProps } from "@lib/types/Api";
import { Form, Input, InputNumber, Modal, Space, Typography } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import useIntl from "react-intl/lib/src/components/useIntl";
import { manualTransactionSchema as yupSchema } from "@lib/schemas/manualTransaction";
import { createYupSync } from "@lib/helpers/TableHelper";
import { FormattedMessage } from "react-intl";
import { convertNumericFields } from "@lib/types/General";
import { CurrencyEnum, ManualTransaction } from "@lib/types/Transaction";
import { Customer } from "@lib/types/Customer";
import { useQueryClient } from "@tanstack/react-query";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props<T> = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery?: UseMutationProps<Partial<T>>;
  invoiceQueryKey?: string[];
  selected?: T;
  invoiceId?: string;
  customer: Customer;
};

export default function ManualTransactionModal<T extends ManualTransaction>({
  openModal,
  toggleModal,
  mutationQuery,
  invoiceQueryKey,
  selected,
  invoiceId,
  customer,
}: Props<T>) {
  const { messages } = useIntl();
  const [form] = Form.useForm();

  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = mutationQuery;

  const initialValues = useMemo(() => {
    return selected
      ? convertNumericFields(selected)
      : ({
          amount: 0,
          description: "",
          currency: CurrencyEnum.USD,
        } as Partial<T>);
  }, [selected]);

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
  }, [initialValues]);

  const handleClose = useCallback(() => {
    handleResetForm();
    toggleModal(false);
  }, [toggleModal]);

  useEffect(() => {
    if (!openModal) return;

    handleResetForm();
  }, [openModal]);

  return (
    <Modal
      title={
        <FormattedMessage
          id={selected ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.cashPayments"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
    >
      <Form<T>
        {...layout}
        form={form}
        name={"product-modal"}
        style={{ maxWidth: 600, marginTop: 20 }}
        autoComplete="off"
        initialValues={{ enabled: true, ...selected }}
        onFinish={(values) =>
          mutateAsync({
            ...values,
            customerId: customer.id,
            invoiceId,
            currency: CurrencyEnum.USD,
            id: selected?.id,
          }).finally(() => {
            if (invoiceQueryKey) {
              queryClient.invalidateQueries({
                queryKey: invoiceQueryKey,
              });
            }
          })
        }
      >
        <Form.Item
          name="amount"
          label={messages["common.amount"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <InputNumber
            style={{ width: "100%" }}
            addonAfter={<Typography>{CurrencyEnum.USD}</Typography>}
            autoFocus={true}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={messages["common.description"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input.TextArea />
        </Form.Item>

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            {!selected && (
              <ActionButton
                type="reset"
                onClick={handleResetForm}
                loading={isPending}
              />
            )}

            <ActionButton
              type={!!selected ? "save" : "add"}
              loading={isPending}
            />
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
